/**
 * Vector shape generators — Tier 2 Group A.
 *
 * Pure functions that produce a `VectorPath` for the new shape
 * primitives: regular polygons, stars, and arrows. Each generator
 * accepts a center point + a target size and emits a closed (for
 * polygon / star) or open (for arrow) sub-path whose anchors are
 * placed in scene-space coordinates.
 *
 * The Shape tool branches on `ShapeType`:
 *   - 'rect' / 'circle' / 'line' / 'triangle'  → Fabric primitive
 *     via `canvas.addShape` (legacy raster-like layers).
 *   - 'polygon' / 'star' / 'arrow'             → vector layer via
 *     `canvas.addVectorObject` using one of these generators.
 *
 * Pure module — no DOM, no React, no Fabric.
 */

import type { LayerNode, ShapeType, VectorPath, VectorAnchor } from '../types';

/** Decide whether a slider-driven shape-settings change should
 *  regenerate the active vector primitive's path. Returns true
 *  ONLY when:
 *    - the layer is a vector node, AND
 *    - it carries a `vectorSource` marker (i.e. it was created
 *      by the polygon / star / arrow generator, NOT free-pen-
 *      drawn), AND
 *    - the slider's shape type matches the layer's source kind
 *      (cross-kind switches don't morph a polygon into a rect).
 *
 *  Pure helper — extracted from EditorPage's shapeSettings effect
 *  so the gate's contract can be tested without renderHook /
 *  fabric mocks. Trip-wire: any future change to the regenerate
 *  flow must keep this gate in lockstep with what EditorPage
 *  actually does. */
export function shouldRegenerateVectorPrimitive(
  node: LayerNode | undefined,
  shapeType: ShapeType,
): boolean {
  if (!node) return false;
  if (node.type !== 'vector') return false;
  if (!node.vectorSource) return false;
  if (shapeType !== 'polygon' && shapeType !== 'star' && shapeType !== 'arrow') return false;
  return node.vectorSource.kind === shapeType;
}

export interface ShapeGeneratorCenter {
  x: number;
  y: number;
}

export interface PolygonOptions {
  /** Number of sides — 3..32. Out-of-range inputs clamp. */
  sides: number;
  /** Inscribed radius (distance from center to a vertex), scene
   *  pixels. */
  radius: number;
  /** Rotation in radians applied to the polygon as a whole. 0 = a
   *  vertex points straight up. */
  rotation?: number;
  /** Corner-rounding radius (scene px). When > 0 each vertex is
   *  replaced with a smooth-tangent curve of the given radius.
   *  Clamped to half the shortest edge length so the rounding
   *  can't cross neighboring vertices. */
  cornerRadius?: number;
}

export interface StarOptions {
  /** Number of points — 3..32. Out-of-range inputs clamp. */
  points: number;
  /** Outer-vertex radius (scene px). */
  outerRadius: number;
  /** Inner-vertex radius as a fraction of `outerRadius`. 0.5 is a
   *  classic 5-point star; 0.95 makes it nearly a polygon, 0.1
   *  makes it spiky. Clamped to (0, 1). */
  innerRadiusRatio: number;
  rotation?: number;
}

export type ArrowHeadShape = 'triangle' | 'open' | 'diamond';

export interface ArrowOptions {
  /** Start of the arrow shaft, scene coords. */
  from: ShapeGeneratorCenter;
  /** Tip of the arrow head (end of the shaft + head), scene
   *  coords. The shaft runs from `from` to the base of the head. */
  to: ShapeGeneratorCenter;
  /** Width of the arrow head, scene px. */
  headSize: number;
  headShape: ArrowHeadShape;
  /** Width of the shaft, scene px. Drives the half-width of the
   *  shaft rectangle. */
  lineWidth: number;
}

/** Build a regular-polygon path. `sides` vertices at evenly-spaced
 *  angles around the center; optional corner rounding replaces each
 *  vertex with a smooth-tangent curve. */
export function generatePolygonPath(
  center: ShapeGeneratorCenter,
  opts: PolygonOptions,
): VectorPath {
  const sides = clampInt(opts.sides, 3, 32);
  const radius = Math.max(1, opts.radius);
  const rotation = opts.rotation ?? -Math.PI / 2;
  const corner = Math.max(0, opts.cornerRadius ?? 0);

  // Vertex positions in scene coords.
  const verts: ShapeGeneratorCenter[] = [];
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides;
    verts.push({
      x: center.x + Math.cos(a) * radius,
      y: center.y + Math.sin(a) * radius,
    });
  }
  if (corner <= 0) {
    return closedPathFromCorners(verts);
  }

  // Each edge has length 2 * radius * sin(pi/sides). Clamp corner
  // radius to half the shortest edge so we don't cross neighbors.
  const edgeLen = 2 * radius * Math.sin(Math.PI / sides);
  const r = Math.min(corner, edgeLen / 2 - 0.5);
  const anchors: VectorAnchor[] = [];
  for (let i = 0; i < sides; i++) {
    const prev = verts[(i - 1 + sides) % sides];
    const cur = verts[i];
    const next = verts[(i + 1) % sides];
    // Tangent direction in/out at this vertex.
    const inDir = normalize({ x: cur.x - prev.x, y: cur.y - prev.y });
    const outDir = normalize({ x: next.x - cur.x, y: next.y - cur.y });
    // Anchor placed BEFORE the vertex by `r` along inDir + a
    // pair of handles that produce a cubic with the standard
    // circle-approximation constant.
    const ax = cur.x - inDir.x * r;
    const ay = cur.y - inDir.y * r;
    const bx = cur.x + outDir.x * r;
    const by = cur.y + outDir.y * r;
    const k = (4 / 3) * Math.tan(Math.PI / (2 * sides));
    // Single anchor at the midpoint of the rounded turn. Two
    // separate anchors (a, b) connected by a cubic also works but
    // doubles the anchor count — pick the cleaner shape.
    const mid = { x: (ax + bx) / 2, y: (ay + by) / 2 };
    anchors.push({
      x: mid.x,
      y: mid.y,
      // Tangent points along the chord ax→bx; magnitude scaled by
      // the standard k for circular approximation.
      inX: -outDir.x * r * k,
      inY: -outDir.y * r * k,
      outX: outDir.x * r * k,
      outY: outDir.y * r * k,
      type: 'smooth',
    });
  }
  return { subPaths: [{ closed: true, anchors }] };
}

/** Build a star path with alternating outer / inner vertices. */
export function generateStarPath(center: ShapeGeneratorCenter, opts: StarOptions): VectorPath {
  const points = clampInt(opts.points, 3, 32);
  const outer = Math.max(1, opts.outerRadius);
  const inner = outer * Math.max(0.01, Math.min(0.99, opts.innerRadiusRatio));
  const rotation = opts.rotation ?? -Math.PI / 2;
  const verts: ShapeGeneratorCenter[] = [];
  const step = Math.PI / points;
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rotation + i * step;
    verts.push({ x: center.x + Math.cos(a) * r, y: center.y + Math.sin(a) * r });
  }
  return closedPathFromCorners(verts);
}

/** Build an arrow path: a rectangular shaft + a head at `to`. The
 *  result is a single closed sub-path that outlines the whole
 *  arrow silhouette so a fill renders correctly. */
export function generateArrowPath(opts: ArrowOptions): VectorPath {
  const { from, to, headSize, headShape, lineWidth } = opts;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len < 1) {
    // Degenerate input — emit a tiny placeholder so callers always
    // get a valid VectorPath back. Real arrows clamp their input
    // upstream; this is a safety valve.
    return { subPaths: [{ closed: true, anchors: [{ x: from.x, y: from.y, type: 'corner' }] }] };
  }
  const ux = dx / len;
  const uy = dy / len;
  // Perpendicular (left of direction).
  const px = -uy;
  const py = ux;
  const halfShaft = Math.max(0.5, lineWidth / 2);
  const headBase = {
    x: to.x - ux * headSize,
    y: to.y - uy * headSize,
  };
  const halfHead = Math.max(halfShaft, headSize / 2);

  // Shaft corners (back-left, back-right) and where the shaft
  // meets the head base on each side.
  const backL = { x: from.x + px * halfShaft, y: from.y + py * halfShaft };
  const backR = { x: from.x - px * halfShaft, y: from.y - py * halfShaft };
  const baseL = { x: headBase.x + px * halfShaft, y: headBase.y + py * halfShaft };
  const baseR = { x: headBase.x - px * halfShaft, y: headBase.y - py * halfShaft };
  // Head outer corners (where the wider triangle base sits) and tip.
  const wingL = { x: headBase.x + px * halfHead, y: headBase.y + py * halfHead };
  const wingR = { x: headBase.x - px * halfHead, y: headBase.y - py * halfHead };
  const tip = { x: to.x, y: to.y };

  let verts: ShapeGeneratorCenter[];
  if (headShape === 'triangle') {
    verts = [backL, baseL, wingL, tip, wingR, baseR, backR];
  } else if (headShape === 'open') {
    // An "open" head is just two angled lines off the shaft — we
    // emit the same silhouette as a triangle but with the wings
    // pulled back closer to the shaft so the head reads as a
    // chevron rather than a filled triangle.
    const chevronL = {
      x: headBase.x + px * (halfShaft + headSize * 0.25),
      y: headBase.y + py * (halfShaft + headSize * 0.25),
    };
    const chevronR = {
      x: headBase.x - px * (halfShaft + headSize * 0.25),
      y: headBase.y - py * (halfShaft + headSize * 0.25),
    };
    verts = [backL, baseL, chevronL, tip, chevronR, baseR, backR];
  } else {
    // Diamond — symmetric wings on both sides of the head base
    // plus the tip, producing a rhombus head.
    const tail = { x: headBase.x + ux * -headSize * 0.25, y: headBase.y + uy * -headSize * 0.25 };
    verts = [backL, baseL, tail, wingL, tip, wingR, tail, baseR, backR];
  }
  return closedPathFromCorners(verts);
}

// ─── helpers ─────────────────────────────────────────────────────

function closedPathFromCorners(verts: ShapeGeneratorCenter[]): VectorPath {
  const anchors: VectorAnchor[] = verts.map((v) => ({ x: v.x, y: v.y, type: 'corner' }));
  return { subPaths: [{ closed: true, anchors }] };
}

function normalize(v: { x: number; y: number }): { x: number; y: number } {
  const len = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / len, y: v.y / len };
}

function clampInt(n: number, min: number, max: number): number {
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.round(n)));
}
